import React from 'react';
import { NameEmailAvatar } from '../../../../../ui';

const TooltipWrapper: React.FC<{ text: string; children?: React.ReactNode }> =
  ({ text, children }) => {
    return (
      <div className='relative group w-full flex items-center justify-start text-left'>
        { /* Truncated Text */ }
        <div className='max-w-[180px] truncate whitespace-nowrap overflow-hidden'>
          { text || '-' }
        </div>

        { /* Tooltip */ }
        <div className='absolute z-[9999] hidden group-hover:flex flex-col items-center'>
          <div className='relative'>
            { /* Tooltip Box */ }
            <div className='absolute bottom-full left-0 px-3 py-2 text-sm text-white bg-gray-900 rounded-md shadow-lg max-w-xs whitespace-normal break-words'>
              { text }
            </div>
            { /* Arrow */ }
            <div className='absolute top-full left-2'>
              <div className='w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900'></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

// Format timestamp to readable date - using utility function
const formatDate = (timestamp: number | undefined): string => {
  if (!timestamp) return '-';
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Format duration in minutes to readable format
const formatDuration = (minutes: number): string => {
  if (!minutes) return '-';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }
  return `${remainingMinutes}m`;
};

// Get assessment type badge color and styling
const getAssessmentTypeBadge = (type: string) => {
  const styles = {
    candidate: 'bg-blue-100 text-blue-800 border-blue-200',
    employee: 'bg-green-100 text-green-800 border-green-200',
    partner: 'bg-purple-100 text-purple-800 border-purple-200',
    default: 'bg-gray-100 text-gray-800 border-gray-200',
  };

  const style = styles[type as keyof typeof styles] || styles.default;

  return (
    <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${style} capitalize`}
    >
      { type || 'Unknown' }
    </span>
  );
};

// Get status badge
const getStatusBadge = (isActive: boolean) => {
  return (
    <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
          isActive
            ? 'bg-green-100 text-green-800 border-green-200'
            : 'bg-red-100 text-red-800 border-red-200'
        }`}
    >
      <div
          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
            isActive ? 'bg-green-500' : 'bg-red-500'
          }`}
      />
      { isActive ? 'Active' : 'Inactive' }
    </span>
  );
};

// Get pre-screen badge
const getPreScreenBadge = (preScreen: any[]) => {
  const hasPreScreen = preScreen && preScreen.length > 0;

  return (
    <div className='flex items-center gap-2'>
      <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
            hasPreScreen
              ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
              : 'bg-gray-100 text-gray-800 border-gray-200'
          }`}
      >
        <div
            className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
              hasPreScreen ? 'bg-emerald-500' : 'bg-gray-500'
            }`}
        />
        { hasPreScreen ? 'Enabled' : 'Disabled' }
      </span>
      { hasPreScreen && (
        <span className='text-xs text-gray-500'>
          ({ preScreen.length } questions)
        </span>
      ) }
    </div>
  );
};
export const getColumns = () => {
  return [
    {
      key: 'id',
      label: 'ID',
      width: '120px',
      align: 'left' as const,
      render: (row: any) => row.id,
      disableSort: true,
    },
    {
      key: 'assessmentTitle',
      label: 'Assessment Title',
      width: '250px',
      align: 'left' as const,
      render: (row: any) => (
        <div className='flex flex-col gap-1'>
          <div className='font-semibold text-gray-900 truncate max-w-[200px]'>
            { row.assessmentTitle || 'Untitled Assessment' }
          </div>
          { row.description && (
            <TooltipWrapper text={row.description}>
              <div className='text-sm text-gray-500 truncate max-w-[200px] cursor-help'>
                { row.description }
              </div>
            </TooltipWrapper>
          ) }
        </div>
      ),
      disableSort: false,
    },
    {
      key: 'assessmentType',
      label: 'Type',
      width: '120px',
      align: 'left' as const,
      render: (row: any) => getAssessmentTypeBadge(row.assessmentType),
      disableSort: false,
    },
    {
      key: 'user',
      label: 'Creator',
      width: '200px',
      align: 'left' as const,
      render: (row: any) => (
        <NameEmailAvatar
            email={row.user?.email || 'N/A'}
            name={
              `${row.user?.firstName || ''} ${row.user?.lastName || ''}`.trim() ||
            'Unknown User'
            }
            avatar={row.user?.profilePic}
        />
      ),
      disableSort: false,
    },
    {
      key: 'jobPosition',
      label: 'Job Position',
      width: '250px',
      align: 'left' as const,
      render: (row: any) => (
        <div className='flex flex-col'>
          <span className='font-medium text-gray-900 truncate max-w-[120px]'>
            { row.jobPosition || 'Not Specified' }
          </span>
          <span className='text-xs text-gray-500'>
            { row.totalScore ? `${row.totalScore} points` : 'No scoring' }
          </span>
        </div>
      ),
      disableSort: false,
    },
    {
      key: 'totalTime',
      label: 'Duration',
      width: '100px',
      align: 'left' as const,
      render: (row: any) => (
        <div className='flex items-center gap-2'>
          <span className='text-sm font-medium text-gray-900'>
            { formatDuration(row.totalTime) }
          </span>
          { row.isStrictDuration && (
            <span className='inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800'>
              Strict
            </span>
          ) }
        </div>
      ),
      disableSort: false,
    },
    {
      key: 'startDate',
      label: 'Start Date',
      width: '120px',
      align: 'left' as const,
      render: (row: any) => (
        <div className='text-sm text-gray-900'>{ formatDate(row.startDate) }</div>
      ),
      disableSort: false,
    },
    {
      key: 'endDate',
      label: 'End Date',
      width: '120px',
      align: 'left' as const,
      render: (row: any) => (
        <div className='text-sm text-gray-900'>{ formatDate(row.endDate) }</div>
      ),
      disableSort: false,
    },
    {
      key: 'creationDateTime',
      label: 'Created',
      width: '120px',
      align: 'left' as const,
      render: (row: any) => (
        <div className='text-sm text-gray-900'>
          { formatDate(row.creationDateTime) }
        </div>
      ),
      disableSort: false,
    },
    {
      key: 'preScreen',
      label: 'Pre-Screen',
      width: '130px',
      align: 'left' as const,
      render: (row: any) => getPreScreenBadge(row.preScreen),
      disableSort: false,
    },
    {
      key: 'isActive',
      label: 'Status',
      width: '100px',
      align: 'left' as const,
      render: (row: any) => getStatusBadge(row.isActive),
      disableSort: false,
    },
  ];
};
